import Axios from "axios"
import store from "@/store"
import { getCookie, setCookie } from "@/helpers/customizeCookie.js"
import { decodeJwtClaims } from "@/helpers/jwtClaims.js"

// Step-up 2FA (OTP Telegram).
// - Sau login: nếu token có mfa_required=true mà mfa=false -> bắt buộc xác thực ngay.
// - Khi backend id trả 401 error_code "mfa_required" -> mở dialog rồi gửi lại request.
// Nhiều nơi cùng yêu cầu chỉ mở 1 dialog: tất cả chờ chung một promise.
let pending = null

export const currentToken = () =>
  getCookie("token") || localStorage.getItem("token")

export const isMfaRequiredError = (error) => {
  const res = error && error.response
  return !!(
    res &&
    res.status === 401 &&
    res.data &&
    res.data.error_code === "mfa_required"
  )
}

// Token hiện tại thuộc diện bắt buộc OTP nhưng chưa qua OTP.
export const tokenNeedsMfa = (token = currentToken()) => {
  const claims = decodeJwtClaims(token)
  return !!(claims && claims.mfa_required === true && claims.mfa !== true)
}

// Mở dialog; resolve với token mới (mfa=true), reject khi người dùng huỷ.
// mandatory=true: không cho huỷ (chỉ đăng xuất) — dùng ngay sau login.
export const requestMfaStepUp = ({ mandatory = false } = {}) => {
  if (pending) {
    if (mandatory) {
      store.commit("SET_MFA_STEP_UP_MANDATORY", true)
    }
    return pending.promise
  }
  pending = {}
  pending.promise = new Promise((resolve, reject) => {
    pending.resolve = resolve
    pending.reject = reject
  })
  store.commit("SET_MFA_STEP_UP_MANDATORY", mandatory)
  store.commit("SET_MFA_STEP_UP_VISIBLE", true)
  return pending.promise
}

// Gọi sau khi đăng nhập / tải lại trang khi đã đăng nhập.
// Hỏi backend trạng thái hiện tại (GET /api/me/telegram -> mfa_required / admin_mfa_required theo DB) để bắt
// cả phiên cũ có token cấp trước khi user bị bật cờ / tới hạn; lỗi mạng thì dựa vào claim token.
export const promptMfaIfRequired = async () => {
  const claims = decodeJwtClaims(currentToken())
  if (claims && claims.mfa === true) {
    return null
  }
  let required = tokenNeedsMfa()
  try {
    const res = await Axios.get(`/api/me/telegram`)
    const data = res && res.data && res.data.data
    if (data) {
      // admin_mfa_required: admin 2FA (TWO_FACTOR_ADMIN_IDS) luôn phải qua OTP khi vào FE id
      required = data.mfa_required === true || data.admin_mfa_required === true
    }
  } catch (e) {
    // giữ kết quả theo claim token
  }
  if (!required) {
    return null
  }
  return requestMfaStepUp({ mandatory: true }).catch(() => null)
}

// Lưu token mới vào mọi nơi app đang giữ token (giống luồng login).
export const applyNewToken = (token) => {
  setCookie("token", token)
  if (localStorage.getItem("token")) {
    localStorage.setItem("token", token)
  }
  Axios.defaults.headers.Authorization = `Bearer ${token}`
  store.commit("SET_AUTH_TOKEN", `Bearer ${token}`)
}

const closeDialog = () => {
  store.commit("SET_MFA_STEP_UP_VISIBLE", false)
  store.commit("SET_MFA_STEP_UP_MANDATORY", false)
}

export const completeMfaStepUp = (token) => {
  applyNewToken(token)
  closeDialog()
  if (pending) {
    pending.resolve(token)
    pending = null
  }
}

export const cancelMfaStepUp = () => {
  closeDialog()
  if (pending) {
    pending.reject(new Error("mfa_step_up_cancelled"))
    pending = null
  }
}

// Interceptor gọi: chờ step-up xong rồi gửi lại request gốc với token mới (chỉ thử lại 1 lần).
export const retryWithStepUp = (error) => {
  const config = error.config
  if (!config || config._mfaRetried) {
    return Promise.reject(error)
  }
  return requestMfaStepUp().then(
    (token) => {
      config._mfaRetried = true
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` }
      return Axios(config)
    },
    () => Promise.reject(error),
  )
}
